import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';

/**
 * Quiz Leaderboard Component
 * Live leaderboard display for quiz results with animations
 */

const QuizLeaderboard = ({ activity, responses = [], showProjection = false, onExport }) => {
  const [sortBy, setSortBy] = useState('score'); // 'score', 'time', 'name'
  const [animatedScores, setAnimatedScores] = useState([]);

  // Calculate leaderboard from responses
  const calculateLeaderboard = () => {
    if (!responses || responses.length === 0) return [];

    const leaderboard = responses.map((response, index) => {
      // Handle different response formats
      const responseData = response.response || response;

      // Get answers - could be array, number, or nested in response
      let answersArray = [];
      if (Array.isArray(responseData.answers)) {
        answersArray = responseData.answers;
      } else if (Array.isArray(response.answers)) {
        answersArray = response.answers;
      }

      // Calculate correct answers
      let correctAnswers = 0;

      // First check if results object contains the score (from QuizActivity submission)
      const resultsData = responseData.results || response.results;
      if (resultsData && typeof resultsData.percentage === 'number') {
        // Use pre-calculated results from participant's quiz submission
        const totalQuestions = activity?.questions?.length || activity?.config?.questions?.length || 1;
        return {
          id: response.participantId || response.participant || index,
          name: response.participantName || responseData.participantName || response.name || `Participant ${index + 1}`,
          score: Math.round(resultsData.percentage),
          correctAnswers: resultsData.correct || Math.round((resultsData.percentage / 100) * totalQuestions),
          totalQuestions,
          timeSpent: responseData.timeSpent || response.timeSpent || 0,
          completionTime: resultsData.completionTime || '0:00',
          passed: resultsData.percentage >= (activity?.passingScore || 70),
          rank: 0,
        };
      }

      if (answersArray.length > 0) {
        correctAnswers = answersArray.filter((a) => a && a.correct).length;
      } else if (typeof responseData.correctAnswers === 'number') {
        correctAnswers = responseData.correctAnswers;
      } else if (typeof responseData.score === 'number') {
        // If we have a pre-calculated score, use it
        const totalQuestions = activity?.questions?.length || activity?.config?.questions?.length || 1;
        return {
          id: response.participantId || response.participant || index,
          name: response.participantName || responseData.participantName || response.name || `Participant ${index + 1}`,
          score: Math.round(responseData.score),
          correctAnswers: Math.round((responseData.score / 100) * totalQuestions),
          totalQuestions,
          timeSpent: responseData.timeSpent || response.timeSpent || 0,
          passed: responseData.score >= (activity?.passingScore || 70),
          rank: 0,
        };
      }

      const totalQuestions = activity?.questions?.length || activity?.config?.questions?.length || answersArray.length || 1;
      const score = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
      const timeSpent = responseData.timeSpent || response.timeSpent || 0;

      return {
        id: response.participantId || response.participant || index,
        name: response.participantName || response.name || `Participant ${index + 1}`,
        score: Math.round(score),
        correctAnswers,
        totalQuestions,
        timeSpent,
        passed: score >= (activity?.passingScore || 70),
        rank: 0, // Will be calculated after sorting
      };
    });

    // Sort based on selected criteria
    let sorted = [...leaderboard];
    if (sortBy === 'score') {
      sorted.sort((a, b) => b.score - a.score || a.timeSpent - b.timeSpent);
    } else if (sortBy === 'time') {
      sorted.sort((a, b) => a.timeSpent - b.timeSpent || b.score - a.score);
    } else if (sortBy === 'name') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }

    // Assign ranks
    sorted.forEach((entry, index) => {
      entry.rank = index + 1;
    });

    return sorted;
  };

  const leaderboard = calculateLeaderboard();

  // Animate scores on mount and updates
  useEffect(() => {
    setAnimatedScores(leaderboard.map((entry) => ({ ...entry, displayScore: 0 })));

    const timer = setTimeout(() => {
      setAnimatedScores(leaderboard);
    }, 100);

    return () => clearTimeout(timer);
  }, [responses, sortBy]);

  // Format time as human-readable (e.g., "5m 18s" or "45s")
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins === 0) {
      return `${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  // Parse completion time string (e.g., "5:17") to seconds
  const parseTimeToSeconds = (timeStr) => {
    if (!timeStr) return 0;
    if (typeof timeStr === 'number') return timeStr;
    const parts = timeStr.toString().split(':');
    if (parts.length === 2) {
      const mins = parseInt(parts[0], 10) || 0;
      const secs = parseInt(parts[1], 10) || 0;
      return mins * 60 + secs;
    }
    return 0;
  };

  // Format completion time string (e.g., "5:17") to human-readable (e.g., "5m 17s")
  const formatCompletionTime = (timeStr) => {
    if (!timeStr) return '0s';
    if (typeof timeStr === 'number') return formatTime(timeStr);
    const parts = timeStr.toString().split(':');
    if (parts.length === 2) {
      const mins = parseInt(parts[0], 10) || 0;
      const secs = parseInt(parts[1], 10) || 0;
      if (mins === 0) return `${secs}s`;
      return `${mins}m ${secs}s`;
    }
    return timeStr; // Return as-is if not in expected format
  };

  // Get medal emoji
  const getMedal = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  // Get rank badge color
  const getRankColor = (rank) => {
    if (rank === 1) return 'bg-amber-100 text-amber-700 border-amber-300';
    if (rank === 2) return 'bg-neutral-100 text-neutral-700 border-neutral-300';
    if (rank === 3) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-neutral-50 text-neutral-600 border-neutral-200';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Quiz Leaderboard</h3>
            <p className="text-sm text-neutral-600">
              {leaderboard.length} {leaderboard.length === 1 ? 'participant' : 'participants'}
              {activity?.passingScore && ` • ${leaderboard.filter((e) => e.passed).length} passed`}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Sort Options */}
            <div className="flex bg-neutral-100 rounded-lg p-1">
              <button
                onClick={() => setSortBy('score')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  sortBy === 'score'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Score
              </button>
              <button
                onClick={() => setSortBy('time')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  sortBy === 'time'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Time
              </button>
              <button
                onClick={() => setSortBy('name')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  sortBy === 'name'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                Name
              </button>
            </div>

            {/* Export Button */}
            {onExport && (
              <Button variant="secondary" size="sm" onClick={() => onExport(leaderboard, 'quiz')}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </Button>
            )}
          </div>
        </div>

        {/* Leaderboard List */}
        {leaderboard.length > 0 ? (
          <div className="space-y-2">
            <AnimatePresence mode="popLayout">
              {animatedScores.map((entry, index) => (
                <motion.div
                  key={entry.id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    entry.rank <= 3
                      ? getRankColor(entry.rank)
                      : 'bg-white border-neutral-200 hover:border-neutral-300'
                  }`}
                >
                  {/* Rank */}
                  <div className="flex items-center justify-center w-12">
                    {getMedal(entry.rank) ? (
                      <span className="text-3xl">{getMedal(entry.rank)}</span>
                    ) : (
                      <span className="text-lg font-bold text-neutral-400">#{entry.rank}</span>
                    )}
                  </div>

                  {/* Participant Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold text-neutral-900 truncate">{entry.name}</h4>
                      {entry.passed && (
                        <Badge variant="success" size="sm">
                          Passed
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-neutral-600">
                      {entry.correctAnswers}/{entry.totalQuestions} correct • {entry.completionTime ? formatCompletionTime(entry.completionTime) : formatTime(entry.timeSpent)}
                    </p>
                  </div>

                  {/* Score Display */}
                  <div className="flex items-center gap-3">
                    {/* Progress Bar */}
                    <div className="hidden sm:block w-24 bg-neutral-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          entry.passed ? 'bg-green-500' : 'bg-amber-500'
                        }`}
                        style={{ width: `${entry.score}%` }}
                      ></div>
                    </div>

                    {/* Score */}
                    <div className="text-right">
                      <div
                        className={`text-2xl font-bold ${
                          entry.passed ? 'text-green-600' : 'text-amber-600'
                        }`}
                      >
                        {entry.score}%
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 bg-neutral-50 rounded-lg">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto text-neutral-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              <p className="text-neutral-600">No quiz submissions yet</p>
              <p className="text-sm text-neutral-500">The leaderboard will update in real-time</p>
            </div>
          </div>
        )}
      </Card>

      {/* Statistics */}
      {leaderboard.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card padding="md">
            <div className="text-center">
              <p className="text-sm text-neutral-600 mb-1">Average Score</p>
              <p className="text-2xl font-bold text-primary-600">
                {Math.round(leaderboard.reduce((sum, e) => sum + e.score, 0) / leaderboard.length)}%
              </p>
            </div>
          </Card>

          <Card padding="md">
            <div className="text-center">
              <p className="text-sm text-neutral-600 mb-1">Highest Score</p>
              <p className="text-2xl font-bold text-green-600">
                {Math.max(...leaderboard.map((e) => e.score))}%
              </p>
            </div>
          </Card>

          <Card padding="md">
            <div className="text-center">
              <p className="text-sm text-neutral-600 mb-1">Pass Rate</p>
              <p className="text-2xl font-bold text-amber-600">
                {Math.round((leaderboard.filter((e) => e.passed).length / leaderboard.length) * 100)}%
              </p>
            </div>
          </Card>

          <Card padding="md">
            <div className="text-center">
              <p className="text-sm text-neutral-600 mb-1">Avg. Time</p>
              <p className="text-2xl font-bold text-neutral-900">
                {formatTime(Math.round(leaderboard.reduce((sum, e) => {
                  // Use completionTime if available, otherwise timeSpent
                  const time = e.completionTime ? parseTimeToSeconds(e.completionTime) : e.timeSpent;
                  return sum + time;
                }, 0) / leaderboard.length))}
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Projection Mode Indicator */}
      {showProjection && (
        <div className="fixed top-4 right-4 bg-neutral-800 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm">
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
          <span>Projecting</span>
        </div>
      )}
    </div>
  );
};

export default QuizLeaderboard;
